import React, { useState, useEffect } from 'react';

interface Account {
  id: number;
  email: string;
  role: string;
  plan: string;
  associatedUserId: number | null;
  createdAt: string;
  isMain: boolean;
  isCurrent: boolean;
}

interface PlanSelectorProps {
  onPlanChange: (accountId: number) => void;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({ onPlanChange }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [hasMultipleAccounts, setHasMultipleAccounts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    const fetchAssociatedAccounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/user/associated-accounts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setHasMultipleAccounts(data.hasMultipleAccounts);
          setAccounts(data.accounts || []);
        }
      } catch (error) {
        console.error('Error al obtener cuentas asociadas:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssociatedAccounts();
  }, []);

  const handlePlanChange = async (accountId: number) => {
    if (isChanging) return;
    
    setIsChanging(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/generate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ accountId })
      });
      
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onPlanChange(accountId);
      } else {
        console.error('Error al cambiar de plan');
      }
    } catch (error) {
      console.error('Error de red al cambiar plan:', error);
    } finally {
      setIsChanging(false);
      setShowDropdown(false);
    }
  };

  const getPlanDisplayName = (plan: string) => {
    if (plan.startsWith('SEO')) {
      const count = plan.includes('_2M') ? '2' : '4';
      return `SEO - ${count} publicaciones/mes`;
    } else if (plan.startsWith('RRSS')) {
      const count = plan.includes('_4M') ? '4' : plan.includes('_6M') ? '6' : '12';
      return `Redes Sociales - ${count} publicaciones/mes`;
    }
    return plan;
  };

  const getPlanIcon = (plan: string) => {
    if (plan.startsWith('SEO')) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    } else if (plan.startsWith('RRSS')) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      );
    }
    return null;
  };

  const getPlanColor = (plan: string) => {
    if (plan.startsWith('SEO')) {
      return 'text-green-600';
    } else if (plan.startsWith('RRSS')) {
      return 'text-blue-600';
    }
    return 'text-gray-600';
  };

  if (isLoading) {
    return null;
  }

  if (!hasMultipleAccounts || accounts.length <= 1) {
    return null;
  }

  const currentAccount = accounts.find(acc => acc.isCurrent);

  const buttonClass = "flex items-center gap-2 w-full px-4 py-3 bg-white rounded-xl text-sm font-medium text-blue-600 disabled:opacity-50 border-2 border-blue-400 hover:bg-blue-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-200 text-left shadow-none";

  // Función interna para renderizar el botón y el dropdown
  const renderButton = () => (
    <div className="relative w-full">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={isChanging}
        className={buttonClass}
        style={{ boxShadow: 'none' }}
      >
        <span className={`font-medium ${getPlanColor(currentAccount?.plan || '')} flex items-center gap-2`}>
          {getPlanIcon(currentAccount?.plan || '')}
          {getPlanDisplayName(currentAccount?.plan || '')}
        </span>
        <span className="flex-1" />
        <svg 
          className={`w-4 h-4 transition-transform duration-200 text-blue-400 ${showDropdown ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {/* Dropdown */}
      {showDropdown && (
        <div className="z-50 w-full max-w-full sm:max-w-xs bg-white border border-gray-200 rounded-lg shadow-lg absolute left-0 right-0 top-full mt-2">
          <div className="p-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800 text-sm">Cambiar de plan</h3>
            <p className="text-gray-500 text-xs mt-1">Selecciona el plan con el que quieres trabajar</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {accounts.map((account) => (
              <button
                key={account.id}
                onClick={() => handlePlanChange(account.id)}
                disabled={account.isCurrent || isChanging}
                className={`w-full p-3 text-left hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0 flex items-center gap-3 ${
                  account.isCurrent ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                } ${isChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  account.isCurrent ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  {getPlanIcon(account.plan)}
                </div>
                <span className={`font-medium text-sm ${getPlanColor(account.plan)}`}>{getPlanDisplayName(account.plan)}</span>
                {account.isCurrent && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    Actual
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Overlay para cerrar dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );

  return (
    <div className="w-full">
      {renderButton()}
    </div>
  );
};

export default PlanSelector; 